import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Bell, Plus, Trash2, CheckCircle, Info, AlertTriangle, 
  AlertCircle, Check, RefreshCw, Mail
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

// Notification type styles
const typeStyles = {
  'info': { icon: Info, bg: 'bg-blue-100', text: 'text-blue-800', iconColor: 'text-blue-600' },
  'success': { icon: CheckCircle, bg: 'bg-emerald-100', text: 'text-emerald-800', iconColor: 'text-emerald-600' },
  'warning': { icon: AlertTriangle, bg: 'bg-amber-100', text: 'text-amber-800', iconColor: 'text-amber-600' },
  'error': { icon: AlertCircle, bg: 'bg-red-100', text: 'text-red-800', iconColor: 'text-red-600' },
};

export default function NotificationCenter() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    notification_type: 'info',
    target_role: 'all'
  });

  const isAdmin = user?.role === 'admin';
  const userId = user?.id;

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    const token = getToken();
    
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.unread_count);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    const token = getToken();
    try {
      await axios.post(`${API}/api/notifications`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notification sent');
      setShowCreateModal(false);
      setFormData({ title: '', message: '', notification_type: 'info', target_role: 'all' });
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to create notification');
    }
  };

  const handleMarkRead = async (id) => {
    const token = getToken();
    try {
      await axios.post(`${API}/api/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllRead = async () => {
    const token = getToken();
    try {
      await axios.post(`${API}/api/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All notifications marked as read');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notification?')) return;
    
    const token = getToken();
    try {
      await axios.delete(`${API}/api/notifications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notification deleted');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const isUnread = (notification) => {
    return !notification.read_by?.includes(userId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="notification-center">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bell className="w-7 h-7" />
            Notification Center
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white ml-2">{unreadCount} new</Badge>
            )}
          </h1>
          <p className="text-slate-500">View and manage system notifications</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchNotifications}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={handleMarkAllRead}>
              <Check className="w-4 h-4 mr-2" />
              Mark All Read
            </Button>
          )}
          {isAdmin && (
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New Notification
            </Button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {notifications.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700">No Notifications</h3>
              <p className="text-slate-500">You&apos;re all caught up!</p>
            </CardContent>
          </Card>
        ) : (
          notifications.map((notification) => {
            const typeStyle = typeStyles[notification.notification_type] || typeStyles.info;
            const TypeIcon = typeStyle.icon;
            const unread = isUnread(notification);
            
            return (
              <Card 
                key={notification.id} 
                className={`transition-all ${unread ? 'border-l-4 border-l-blue-500 bg-blue-50/30' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg ${typeStyle.bg} flex items-center justify-center flex-shrink-0`}>
                      <TypeIcon className={`w-5 h-5 ${typeStyle.iconColor}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                            {notification.title}
                            {unread && (
                              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                            )}
                          </h4>
                          <p className="text-sm text-slate-600 mt-1">{notification.message}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                            <span>{new Date(notification.created_at).toLocaleString()}</span>
                            <Badge variant="outline" className="text-xs capitalize">
                              {notification.target_role === 'all' ? 'Everyone' : notification.target_role}
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          {unread && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkRead(notification.id)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(notification.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Notification Modal (Admin Only) */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Notification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Notification title"
              />
            </div>

            <div className="space-y-2">
              <Label>Message *</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Notification message..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={formData.notification_type} 
                  onValueChange={(v) => setFormData({ ...formData, notification_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Target Audience</Label>
                <Select 
                  value={formData.target_role} 
                  onValueChange={(v) => setFormData({ ...formData, target_role: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Everyone</SelectItem>
                    <SelectItem value="admin">Admins Only</SelectItem>
                    <SelectItem value="agent">Agents Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate}>
              <Mail className="w-4 h-4 mr-2" />
              Send Notification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
